import { ComponentPropsWithoutRef, Suspense, useContext } from 'react';
import { Button } from '@givebutter/primitives/button';
import { pluralize } from '@givebutter/util';
import { faBarsFilter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClearFiltersButton } from 'components/filter/ClearFiltersButton';
import { FilterGroupProvider } from 'components/filter/filters-next/FilterGroupContext';
import {
    FilterProvider,
    FilterProviderContext,
} from 'components/filter/filters-next/FilterProvider';
import { QuickFiltersBar } from 'components/filter/filters-next/QuickFiltersBar';
import { useFlattenedTableColumnFilters } from 'components/filter/filters-next/useFlattenedTableColumnFilters';
import { useBidirectionalTableAndFilterCacheSync } from 'components/filter/filters-next/useSyncFilters';
import { TableContext } from 'components/table/TableContext';
import { getTableResourceFromReportResource } from 'components/table/util';
import { useReportSchemas } from 'customHooks/useReportSchemas';
import { twJoin, twMerge } from 'tailwind-merge';

interface TableFiltersBarProps extends ComponentPropsWithoutRef<'div'> {}

const TableFiltersBar = (props: TableFiltersBarProps) => {
    const { className = '', ...rest } = props;
    const { table } = useContext(TableContext);
    const { resource } = useContext(FilterProviderContext);
    const tableColumns = useFlattenedTableColumnFilters();
    const { data: schemas } = useReportSchemas();
    const isExpanded = table.getState().isFilterBarExpanded;
    const setIsExpanded = table.updateIsFilterBarExpanded;
    const currentEditingColumn = table.getState().currentlyEditingColumn;
    const schema = schemas.find(
        schema => getTableResourceFromReportResource(schema.resource) === resource,
    );
    const columns = schema?.schema.columns ?? [];
    const currentlyEditingColumnFromSchema = columns.find(
        col => col.name === currentEditingColumn?.id,
    );

    useBidirectionalTableAndFilterCacheSync('tableFiltersBar');

    const tableColumnLength = tableColumns.length;
    const isEmpty = tableColumns.length === 0;

    if (!schema || isEmpty || currentEditingColumn || currentlyEditingColumnFromSchema) {
        return null;
    }

    return (
        <div
            className={twMerge('flex items-center gap-2.5 whitespace-nowrap p-2', className)}
            {...rest}
        >
            {!isEmpty && (
                <Button
                    variant="none"
                    color="neutral"
                    size="sm"
                    className={twJoin(
                        !isExpanded && 'hover:bg-gray-100',
                        isExpanded && 'bg-gray-200 hover:bg-gray-300',
                        'h-6 rounded px-2 font-medium text-gray-500',
                    )}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <FontAwesomeIcon icon={faBarsFilter} />
                    {tableColumnLength} {pluralize(tableColumnLength, 'filter')}
                </Button>
            )}
            {isExpanded && (
                <Suspense fallback={null}>
                    <FilterProvider resource={'tableFiltersBar'} cacheKey={'tableFiltersBar'}>
                        <FilterGroupProvider showInnerOperator={false} showOuterOperator={false}>
                            <div className={'flex flex-wrap gap-1.5'}>
                                <QuickFiltersBar />
                                <ClearFiltersButton />
                            </div>
                        </FilterGroupProvider>
                    </FilterProvider>
                </Suspense>
            )}
        </div>
    );
};

export { TableFiltersBar };
export type { TableFiltersBarProps };
